import { AddIcon } from '@chakra-ui/icons';
import { FormControl, FormLabel, HStack } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useTRSClusters from './hooks/useTRSClusters';
import useGetTRSNodes from './queries/useGetTRSNodes';

import { Button, Text } from '@/components/v4';
import { dropDownFormStyle } from 'components/forms/new/formStyles';
import { Error } from 'components/index';
import ActionsButton from 'components/tables/AGGrid/ActionsButton';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';

export default function TRSNodes() {
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { clusterID = '' } = location.state || {};

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const [selectedCluster, setSelectedCluster] = useState(null);
    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    // Fetch clusters for filter dropdown
    const {
        data: clusters = [],
        isLoading: clustersLoading,
        error: clustersError,
    } = useTRSClusters();

    const handleEditNode = useCallback(
        (nodeData) => {
            navigate(`/trs-nodes/edit/${nodeData.id}`, {
                state: {
                    clusterID: selectedCluster?.id,
                },
            });
        },
        [navigate, selectedCluster?.id],
    );

    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Cluster Name',
                field: 'clusterName',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.clusterName,
                flex: 1,
                minWidth: 200,
            },
            {
                headerName: 'Capacity',
                field: 'capacity',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.capacity,
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => {
                    return (
                        <ActionsButton
                            actions={[
                                {
                                    label: 'Edit Node',
                                    onClick: () => handleEditNode(params.data),
                                },
                            ]}
                        />
                    );
                },
            },
        ],
        [handleEditNode],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetTRSNodes(
        setTableData,
        gridRef,
        baseColumns,
        selectedCluster,
    );

    // The cluster list has to resolve before nodes can be requested, so treat
    // it as part of the same in-flight window.
    const isFetchingNodes = isFetching || clustersLoading;

    const titleName = 'TRS Nodes';

    useEffect(() => {
        if (clusters.length > 0 && !selectedCluster) {
            if (clusterID) {
                const preSelectedCluster = clusters.find(
                    (c) => c.id === clusterID,
                );
                setSelectedCluster(preSelectedCluster);
            } else {
                setSelectedCluster(clusters[0]);
            }
        }
    }, [clusters, selectedCluster, clusterID]);

    const handleClusterChange = (selectedOption) => {
        setSelectedCluster(selectedOption);
    };

    const handleAddNode = () => {
        navigate('/trs-nodes/add', {
            state: { clusterID: selectedCluster?.id },
        });
    };

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (clustersError) {
        return (
            <Error
                error={{
                    name: 'Failed to load clusters',
                    message: 'Please refresh the page to try again.',
                }}
            />
        );
    }

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    TRS Nodes
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isFetching={isFetchingNodes}
                isRefetching={isRefetching}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            minW="110px"
                            leftIcon={<AddIcon />}
                            onClick={handleAddNode}
                            size="sm"
                            padding="6px 12px">
                            Add Node
                        </Button>

                        {/* Cluster Filter */}
                        <FormControl className="!ml-[16px]">
                            <HStack justifyContent="center" w="500px">
                                <FormLabel
                                    className="w-1/6 leading-4"
                                    fontSize="sm"
                                    mt={2}
                                    mr={0}>
                                    Filter by Cluster:
                                </FormLabel>
                                <Select
                                    className="ml-0 w-full"
                                    placeholder="Select cluster"
                                    isLoading={clustersLoading}
                                    value={selectedCluster}
                                    options={clusters}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    onChange={handleClusterChange}
                                    isClearable={false}
                                    chakraStyles={dropDownFormStyle(darkMode)}
                                    selectedOptionColor="brand"
                                />
                            </HStack>
                        </FormControl>
                    </HStack>
                }
            />
        </>
    );
}
